import { useCallback, useEffect, useState } from "react";
import { type UnitsPreference } from "@jackedlogs/shared";
import { apiFetch, ApiError } from "./api";

// The web console's profile read/write for Settings scalars (units, effort,
// sex, age, …). The API's PUT REMOVEs optional fields absent from the body
// (profiles.ts setIfPresent), so every save is a get-merge-PUT of the whole
// doc — the same round-trip mobile's patchProfile does. There's no offline
// cache here (the console is online); the mobile ProfileContext keeps its
// own AsyncStorage warm-cache.

type ProfileDoc = Record<string, unknown>;

export type UseProfileDoc = {
  doc: ProfileDoc | null;
  loading: boolean;
  error: string | null;
  /** get-merge-PUT a single field, then refresh the local doc. Throws on failure. */
  saveField: (field: string, value: unknown) => Promise<void>;
  /** get-merge-PUT several fields at once (e.g. sex + seeded figure). Throws on failure. */
  savePatch: (patch: Record<string, unknown>) => Promise<void>;
  /** Re-pull the doc with no write (RW-18's LoggerHostDeps.refreshProfile —
   *  the MAX-bump / energy-readout paths need the freshest snapshot after a
   *  save, without a pointless get-merge-PUT-of-itself). */
  refresh: () => Promise<void>;
};

export function useProfileDoc(): UseProfileDoc {
  const [doc, setDoc] = useState<ProfileDoc | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch<ProfileDoc>("/api/profiles");
      setDoc(res);
      setError(null);
    } catch (err) {
      setError(`api_error_${(err as ApiError).status ?? "unknown"}`);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const savePatch = useCallback(async (patch: Record<string, unknown>) => {
    const current = await apiFetch<ProfileDoc>("/api/profiles");
    const next = { ...current, ...patch };
    await apiFetch<unknown>("/api/profiles", { method: "PUT", body: next });
    setDoc(next);
  }, []);

  const saveField = useCallback(
    (field: string, value: unknown) => savePatch({ [field]: value }),
    [savePatch],
  );

  return {
    doc,
    loading: doc === null && error === null,
    error,
    saveField,
    savePatch,
    refresh: load,
  };
}

/** The athlete's display unit, from the one field that holds it.
 *  ⛔ WHY THIS IS A FUNCTION AND NOT SIX COPIES OF ONE LINE. The console read
 *  `(doc?.["unitsPreference"] as UnitsPreference | undefined) ?? "kg"` inline
 *  at six call sites — and the seventh and eighth never got written: the
 *  session detail panel and the catalog browser both passed a literal `"kg"`
 *  while `doc` sat in scope two lines above. An athlete in lb read weights
 *  that were wrong by 2.2×, and nothing failed: a hardcoded default is a
 *  valid value of the type. It is the SAME regression Antenore caught on
 *  2026-05-10 (1.23.2 shipped a hardcoded 'kg'), returning by the route that
 *  makes it easy — a derivation you have to remember to copy.
 *  ⭐ The `?? "kg"` fallback stays: the doc is genuinely null while loading,
 *  and kg is the family default. What is gone is the chance to skip the read.
 *  ⚠️ This is the DEFAULT unit only. The per-row kg/lb chip on a set is an
 *  OVERRIDE that dies with its row and must never write back here.
 *  [[feedback_unit_preference_pattern]] */
export function displayUnitOf(doc: ProfileDoc | null): UnitsPreference {
  return (doc?.["unitsPreference"] as UnitsPreference | undefined) ?? "kg";
}
